import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { OrderStatus, PublicOrder } from '@shared/types';
import { Footer, TopBar, copyText, useDocumentTitle } from '../../components/Chrome';
import { usePrefs } from '../../components/Prefs';
import { formatPrice, shopApi } from './api';

const STEPS: { status: OrderStatus; label: string }[] = [
  { status: 'paid', label: 'Paid' },
  { status: 'in_production', label: 'Printing' },
  { status: 'shipped', label: 'Shipped' },
];

/** Stripe redirects here right away, sometimes a moment before its webhook lands, so poll briefly. */
const POLL_MS = 3000;
const POLL_TRIES = 10;

function headline(order: PublicOrder, waited: boolean): { emoji: string; title: string; body: string } {
  if (order.preorder && order.status === 'pending') {
    return { emoji: '🎉', title: "You're on the list!", body: "We'll email you when decks ship. Nothing to pay today." };
  }
  switch (order.status) {
    case 'pending':
      return waited
        ? { emoji: '⏳', title: 'Still confirming your payment', body: 'The bank is taking its time. Refresh this page in a minute.' }
        : { emoji: '⏳', title: 'Confirming your payment...', body: 'This usually takes a few seconds.' };
    case 'paid':
      return { emoji: '🎉', title: 'Shukriya! Your order is in.', body: "We print in small batches and will email you when it ships." };
    case 'in_production':
      return { emoji: '🖨️', title: 'Your deck is at the printer', body: "We'll email you the tracking number when it ships." };
    case 'shipped':
      return { emoji: '📦', title: 'Your deck is on its way!', body: 'Keep the chai ready.' };
    case 'cancelled':
      return { emoji: '🫥', title: 'This order was cancelled', body: 'No payment was taken. You can order again any time.' };
    case 'refunded':
      return { emoji: '↩️', title: 'This order was refunded', body: 'The refund goes back to the card you paid with.' };
  }
}

export function ThanksPage() {
  useDocumentTitle('Your order');
  const [params] = useSearchParams();
  const id = (params.get('order') ?? '').trim();
  const [order, setOrder] = useState<PublicOrder | null>(null);
  const [error, setError] = useState('');
  const [tries, setTries] = useState(0);
  const { toast } = usePrefs();

  useEffect(() => {
    if (!id) {
      setError('This link is missing its order number.');
      return;
    }
    let cancelled = false;
    shopApi
      .order(id)
      .then((o) => !cancelled && setOrder(o))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [id, tries]);

  const waiting = order?.status === 'pending' && !order.preorder;
  useEffect(() => {
    if (!waiting || tries >= POLL_TRIES) return;
    const t = setTimeout(() => setTries((n) => n + 1), POLL_MS);
    return () => clearTimeout(t);
  }, [waiting, tries]);

  if (error) {
    return (
      <>
        <TopBar />
        <main className="page stack">
          <h1>Order not found</h1>
          <p className="muted">{error}</p>
          <Link to="/shop">Back to the shop</Link>
        </main>
      </>
    );
  }
  if (!order) return <TopBar />;

  const h = headline(order, tries >= POLL_TRIES);
  const reached = STEPS.findIndex((s) => s.status === order.status);
  const showSteps = !order.preorder && ['paid', 'in_production', 'shipped'].includes(order.status);
  return (
    <>
      <TopBar />
      <main className="page stack shop">
        <section className="panel stack center order-hero" role="status" aria-live="polite">
          <span className="big-emoji" aria-hidden="true">
            {h.emoji}
          </span>
          <h1 style={{ margin: 0 }}>{h.title}</h1>
          <p className="muted" style={{ margin: 0 }}>
            {h.body}
          </p>
          {showSteps && (
            <ol className="order-steps">
              {STEPS.map((s, i) => (
                <li key={s.status} className={i <= reached ? 'done' : ''} aria-current={i === reached ? 'step' : undefined}>
                  <span className="dot" aria-hidden="true" />
                  {s.label}
                </li>
              ))}
            </ol>
          )}
          {order.status === 'shipped' && order.tracking && (
            <p style={{ margin: 0 }}>
              Tracking: <strong className="tracking">{order.tracking}</strong>
            </p>
          )}
        </section>

        <section className="panel stack">
          <h2 style={{ margin: 0 }}>Order summary</h2>
          <dl className="order-summary">
            <dt>Deck</dt>
            <dd>
              <Link to={`/shop/${order.productSlug}`}>{order.productName}</Link>
            </dd>
            <dt>Quantity</dt>
            <dd>{order.quantity}</dd>
            <dt>{order.preorder ? 'Price' : 'Total'}</dt>
            <dd>
              {formatPrice(order.amountCents, order.currency)}
              {order.preorder && <span className="muted"> (pay when it ships)</span>}
            </dd>
            {order.customPackCode && (
              <>
                <dt>Your pack</dt>
                <dd>
                  <Link to={`/packs/${order.customPackCode}`}>{order.customPackCode}</Link>
                  <span className="muted small"> · edits made before printing are included</span>
                </dd>
              </>
            )}
            <dt>Order number</dt>
            <dd>
              <code>{order.publicId}</code>
            </dd>
            <dt>Placed</dt>
            <dd>{new Date(order.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</dd>
          </dl>
          <button
            type="button"
            className="btn btn-sm btn-outline"
            style={{ alignSelf: 'flex-start' }}
            onClick={async () => {
              if (await copyText(window.location.href)) toast('Link copied. Bookmark it to check on your order.');
            }}
          >
            Copy link to this page
          </button>
        </section>

        <section className="panel stack center">
          <h2 style={{ margin: 0 }}>While you wait</h2>
          <p className="muted" style={{ margin: 0 }}>
            The game on your phones is free. Start a room and send the code to the family group.
          </p>
          <Link className="btn btn-primary" to="/">
            Start a game
          </Link>
        </section>
      </main>
      <Footer />
    </>
  );
}
